import React, { useContext, useMemo, useState } from "react";
import { CartContext } from "./CartContext";
import "../css/checkout.css";
import productData from "../Datas/productData";
import { Link, useNavigate } from "react-router-dom";
import { CHECKOUT_COUNTRIES, formatMoney, STORE_CONFIG } from "../config/store";

const initialForm = {
  email: "", country: "HK", firstName: "", lastName: "",
  address: "", address2: "", city: "", region: "", postalCode: "",
};

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isRedirecting, setIsRedirecting] = useState(false);

  const lines = useMemo(() => cartItems.map((item) => ({
    ...item,
    product: productData.find((candidate) => candidate.id === item.id),
  })).filter((item) => item.product), [cartItems]);
  const subtotal = lines.reduce((total, item) => total + Number(item.product.price) * item.quantity, 0);
  const total = subtotal + STORE_CONFIG.shippingFee;

  const update = ({ target: { name, value } }) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Enter a valid email address";
    ["country", "firstName", "lastName", "address", "city", "region"].forEach((field) => {
      if (!form[field].trim()) next[field] = "This field is required";
    });
    if (form.country !== "HK" && !form.postalCode.trim()) next.postalCode = "This field is required for this destination";
    return next;
  };

  const createOrderNumber = () => {
    const random = window.crypto && window.crypto.randomUUID
      ? window.crypto.randomUUID().replace(/-/g, "").slice(0, 12)
      : Math.random().toString(36).slice(2, 14);
    return `${STORE_CONFIG.orderNumberPrefix}-${Date.now()}-${random}`.toUpperCase();
  };

  const handlePlaceOrder = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    if (!lines.length) {
      navigate("/cartpage");
      return;
    }

    setIsRedirecting(true);
    const paymentUrl = new URL("/create_payment.php", window.location.origin);
    const successUrl = new URL("/payment?state=success", window.location.origin).toString();
    const cancelUrl = new URL("/payment?state=cancel", window.location.origin).toString();
    const params = {
      orderNo: createOrderNumber(),
      amount: total.toFixed(2),
      currency: STORE_CONFIG.currency,
      email: form.email.trim(),
      success_url: successUrl,
      cancel_url: cancelUrl,
      title: lines[0].product.name,
    };
    Object.entries(params).forEach(([key, value]) => paymentUrl.searchParams.set(key, value));
    window.location.assign(paymentUrl.toString());
  };

  if (!lines.length) {
    return <main className="checkout-empty"><h1>Your cart is empty</h1><Link to="/shop">Return to shop</Link></main>;
  }

  return (
    <main className="main-containerCheck">
      <div className="check-custom-checkout-page">
        <div className="left"><div className="check-order-info">
          <h1>Checkout</h1>
          <p className="checkout-intro">Enter delivery details below. Card information is collected only on the secure hosted payment page.</p>
          <form onSubmit={handlePlaceOrder} noValidate>
            <div className="form-group"><label htmlFor="email">Email</label><input type="email" id="email" name="email" value={form.email} onChange={update} autoComplete="email" />{errors.email && <p className="error">{errors.email}</p>}</div>
            <div className="form-group"><label htmlFor="country">Country / region</label><select id="country" name="country" value={form.country} onChange={update} autoComplete="country">{CHECKOUT_COUNTRIES.map(([code, name]) => <option value={code} key={code}>{name}</option>)}</select>{errors.country && <p className="error">{errors.country}</p>}</div>
            <div className="form-row">
              <div className="form-group"><label htmlFor="firstName">First name</label><input id="firstName" name="firstName" value={form.firstName} onChange={update} autoComplete="given-name" />{errors.firstName && <p className="error">{errors.firstName}</p>}</div>
              <div className="form-group"><label htmlFor="lastName">Last name</label><input id="lastName" name="lastName" value={form.lastName} onChange={update} autoComplete="family-name" />{errors.lastName && <p className="error">{errors.lastName}</p>}</div>
            </div>
            <div className="form-group"><label htmlFor="address">Street address</label><input id="address" name="address" value={form.address} onChange={update} autoComplete="address-line1" />{errors.address && <p className="error">{errors.address}</p>}</div>
            <div className="form-group"><label htmlFor="address2">Apartment, suite, etc. (optional)</label><input id="address2" name="address2" value={form.address2} onChange={update} autoComplete="address-line2" /></div>
            <div className="form-row">
              <div className="form-group"><label htmlFor="city">City</label><input id="city" name="city" value={form.city} onChange={update} autoComplete="address-level2" />{errors.city && <p className="error">{errors.city}</p>}</div>
              <div className="form-group"><label htmlFor="region">State / province / region</label><input id="region" name="region" value={form.region} onChange={update} autoComplete="address-level1" />{errors.region && <p className="error">{errors.region}</p>}</div>
            </div>
            <div className="form-group"><label htmlFor="postalCode">Postal code {form.country === "HK" && <span className="optional-label">(optional in Hong Kong)</span>}</label><input id="postalCode" name="postalCode" value={form.postalCode} onChange={update} autoComplete="postal-code" />{errors.postalCode && <p className="error">{errors.postalCode}</p>}</div>
            <div className="payment-box hosted-payment-box"><strong>{STORE_CONFIG.paymentMethodLabel}</strong><p>Pay Now redirects to {STORE_CONFIG.paymentProvider}'s hosted checkout. Do not enter card details on this page.</p></div>
            <button className="check-checkout-button" type="submit" disabled={isRedirecting}>{isRedirecting ? "Opening secure checkout…" : `Pay Now · ${formatMoney(total)}`}</button>
            <p><Link to="/shipping">Shipping</Link> · <Link to="/returns">Returns</Link> · <Link to="/privacy">Privacy</Link></p>
          </form>
        </div></div>
        <aside className="right" aria-label="Order summary"><div className="check-order-summary"><div className="check-order-details">
          {lines.map((item) => (
            <div key={`${item.id}-${item.size}-${item.color}`} className="check-cart-item">
              <img src={item.product.image} alt={item.product.name} />
              <div className="pro--qty"><div className="qty-inner">{item.quantity}</div></div>
              <div className="check-item-details"><h3>{item.product.name}</h3></div>
              <p className="cal-single">{formatMoney(Number(item.product.price) * item.quantity)}</p>
            </div>
          ))}
          <div className="check-summary-details">
            <div className="summary-row"><span>Subtotal</span><strong>{formatMoney(subtotal)}</strong></div>
            <div className="summary-row"><span>Shipping</span><strong>{formatMoney(STORE_CONFIG.shippingFee)}</strong></div>
            <div className="summary-row total-row"><span>Total</span><strong>{formatMoney(total)}</strong></div>
          </div>
        </div></div></aside>
      </div>
    </main>
  );
};

export default CheckOut;
